import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { CoursesService } from '../shared/services/courses.service';
import { VideosService } from '../shared/services/videos.service';
import { Course, CourseContentItem, CreateCourseRequest, UpdateCourseRequest } from '../shared/models/course';
import { VideoItem } from '../shared/models/video';

@Component({
  selector: 'app-courses',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './courses.component.html',
  styleUrls: ['./courses.component.scss']
})
export class CoursesComponent implements OnInit {
  readonly loading = signal<boolean>(false);
  readonly errorText = signal<string>('');
  readonly courses = signal<Course[]>([]);
  readonly videos = signal<VideoItem[]>([]);
  readonly videoOpen = signal<boolean>(false);
  readonly videoLoading = signal<boolean>(false);
  readonly videoError = signal<string>('');
  private errorTimer: any = null;
  private videoErrorTimer: any = null;

  readonly mode = signal<'list' | 'view' | 'create' | 'edit'>('list');
  readonly selected = signal<Course | null>(null);

  // Dialog state
  readonly viewOpen = signal<boolean>(false);
  readonly editOpen = signal<boolean>(false);
  readonly createOpen = signal<boolean>(false);

  form!: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly coursesService: CoursesService,
    private readonly videosService: VideosService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadCourses();
    this.loadVideos();
  }

  private initForm(): void {
    this.form = this.fb.group({
      COURSE_NAME: ['', [Validators.required, Validators.maxLength(200)]],
      CONTENT: this.fb.array([])
    });
  }

  loadVideos(): void {
    this.videoLoading.set(true);
    this.videoError.set('');
    this.videosService.listVideos().subscribe({
      next: (res) => {
        const list = Array.isArray(res?.body?.videos) ? res.body.videos : [];
        this.videos.set(list);
        this.videoLoading.set(false);
      },
      error: (err) => {
        // Do not block the screen on video list errors; leave dropdown empty
        this.videos.set([]);
        this.setVideoError(this.describeError(err));
        this.videoLoading.set(false);
      }
    });
  }

  openVideoLibrary(): void {
    this.videoOpen.set(true);
    this.loadVideos();
  }

  closeVideoLibrary(refresh = false): void {
    this.videoOpen.set(false);
    if (refresh) {
      this.loadVideos();
    }
  }

  async uploadNewVideoFromFile(input: HTMLInputElement, keyInput?: HTMLInputElement) {
    const file = input.files?.[0];
    if (!file) return;
    const desiredKey = (keyInput?.value || file.name).trim();
    if (!desiredKey) return;
    this.videoLoading.set(true);
    this.videoError.set('');
    try {
      const resp = await this.videosService.presignUploadNew(desiredKey, file.type || undefined).toPromise();
      const url = resp?.body?.url;
      if (!url) throw new Error('Failed to get upload URL');
      const put = await fetch(url, { method: 'PUT', body: file, headers: file.type ? { 'Content-Type': file.type } : {} });
      if (!put.ok) throw new Error('Upload failed');
      this.loadVideos();
      if (keyInput) keyInput.value = '';
      input.value = '';
    } catch (e: any) {
      this.setVideoError(this.describeError(e));
    } finally {
      this.videoLoading.set(false);
    }
  }

  async replaceVideoFromFile(videoKey: string, input: HTMLInputElement) {
    const file = input.files?.[0];
    if (!file) return;
    this.videoLoading.set(true);
    this.videoError.set('');
    try {
      const resp = await this.videosService.presignUploadExisting(videoKey, file.type || undefined).toPromise();
      const url = resp?.body?.url;
      if (!url) throw new Error('Failed to get upload URL');
      const put = await fetch(url, { method: 'PUT', body: file, headers: file.type ? { 'Content-Type': file.type } : {} });
      if (!put.ok) throw new Error('Upload failed');
      this.loadVideos();
      input.value = '';
    } catch (e: any) {
      this.setVideoError(this.describeError(e));
    } finally {
      this.videoLoading.set(false);
    }
  }

  deleteVideo(key: string): void {
    const ok = window.confirm(`Delete video "${key}"?`);
    if (!ok) return;
    this.videoLoading.set(true);
    this.videoError.set('');
    this.videosService.deleteVideo(key).subscribe({
      next: () => {
        this.loadVideos();
      },
      error: (err) => {
        this.setVideoError(this.describeError(err));
        this.videoLoading.set(false);
      }
    });
  }

  prefillKeyFromFile(fileInput: HTMLInputElement, keyInput: HTMLInputElement): void {
    const file = fileInput.files?.[0];
    if (!file) return;
    if (!keyInput.value || !keyInput.value.trim()) {
      keyInput.value = file.name;
    }
  }

  videoExistsKey(key: string): boolean {
    return this.videos().some(v => v.key === key);
  }

  openVideoKey(key: string): void {
    this.videosService.getPresignedUrl(key).subscribe((url) => {
      if (url) window.open(url, '_blank', 'noopener');
    });
  }

  formatSize(bytes: number | null | undefined): string {
    const size = Number(bytes ?? 0);
    if (!isFinite(size) || size <= 0) return '0 B';
    const KB = 1024;
    const MB = KB * 1024;
    const GB = MB * 1024;
    if (size >= GB) return `${(size / GB).toFixed(2)} GB`;
    if (size >= MB) return `${(size / MB).toFixed(2)} MB`;
    if (size >= KB) return `${(size / KB).toFixed(2)} KB`;
    return `${size} B`;
  }

  get contentArray(): FormArray<FormGroup> {
    return this.form.get('CONTENT') as FormArray<FormGroup>;
  }

  private buildContentGroup(item?: Partial<CourseContentItem>): FormGroup {
    return this.fb.group({
      SNO: [item?.SNO ?? '', [Validators.required]],
      CONTENT_TYPE: [item?.CONTENT_TYPE ?? '', [Validators.required]],
      KEY: [item?.KEY ?? '', [Validators.required]]
    });
  }

  addContentRow(): void {
    this.contentArray.push(this.buildContentGroup());
  }

  removeContentRow(index: number): void {
    this.contentArray.removeAt(index);
  }

  loadCourses(): void {
    this.loading.set(true);
    this.errorText.set('');
    this.coursesService.listCourses().subscribe({
      next: (res) => {
        this.courses.set(Array.isArray(res?.body) ? res.body : []);
        this.loading.set(false);
      },
      error: (err) => {
        this.setError(this.describeError(err));
        this.loading.set(false);
      }
    });
  }

  refreshList(): void {
    this.mode.set('list');
    this.selected.set(null);
    this.loadCourses();
  }

  viewCourse(course: Course): void {
    this.selected.set(course);
    this.viewOpen.set(true);
  }

  startCreate(): void {
    this.selected.set(null);
    this.form.reset({ COURSE_NAME: '' });
    this.contentArray.clear();
    this.addContentRow();
    this.createOpen.set(true);
  }

  startEdit(course: Course): void {
    this.selected.set(course);
    this.form.reset({ COURSE_NAME: course.COURSE_NAME });
    this.contentArray.clear();
    for (const item of course.CONTENT ?? []) {
      this.contentArray.push(this.buildContentGroup(item));
    }
    this.editOpen.set(true);
  }

  cancelForm(): void {
    this.mode.set('list');
    this.selected.set(null);
    this.refreshList();
  }

  closeCreateDialog(refresh = false): void {
    this.createOpen.set(false);
    if (refresh) this.refreshList();
  }

  submitCreate(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const payload: CreateCourseRequest = {
      COURSE_NAME: this.form.value.COURSE_NAME,
      CONTENT: (this.form.value.CONTENT ?? []) as CourseContentItem[]
    };
    this.loading.set(true);
    this.coursesService.createCourse(payload).subscribe({
      next: () => this.closeCreateDialog(true),
      error: (err) => {
        this.setError(this.describeError(err));
        this.loading.set(false);
      }
    });
  }

  closeViewDialog(refresh = false): void {
    this.viewOpen.set(false);
    if (refresh) this.refreshList();
  }

  closeEditDialog(refresh = false): void {
    this.editOpen.set(false);
    if (refresh) this.refreshList();
  }

  submitUpdate(): void {
    if (!this.selected()?.COURSE_ID) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const payload: UpdateCourseRequest = {
      COURSE_NAME: this.form.value.COURSE_NAME,
      CONTENT: (this.form.value.CONTENT ?? []) as CourseContentItem[]
    };
    this.loading.set(true);
    this.coursesService.updateCourse(this.selected()!.COURSE_ID, payload).subscribe({
      next: () => this.closeEditDialog(true),
      error: (err) => {
        this.setError(this.describeError(err));
        this.loading.set(false);
      }
    });
  }

  deleteCourse(course: Course): void {
    if (!course?.COURSE_ID) return;
    const ok = window.confirm(`Delete course "${course.COURSE_NAME}"?`);
    if (!ok) return;
    this.loading.set(true);
    this.coursesService.deleteCourse(course.COURSE_ID).subscribe({
      next: () => this.refreshList(),
      error: (err) => {
        this.setError(this.describeError(err));
        this.loading.set(false);
      }
    });
  }

  trackByCourseId(_: number, c: Course) { return c.COURSE_ID; }

  private describeError(err: any): string {
    if (!err) return 'Request failed.';
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    if ((err as any).error?.message) return (err as any).error.message;
    return 'Request failed.';
  }

  private clearError() {
    this.errorText.set('');
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
  }
  dismissError() { this.clearError(); }
  private setError(message: string) {
    this.errorText.set(message);
    if (this.errorTimer) clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => this.clearError(), 10000);
  }

  private clearVideoError() {
    this.videoError.set('');
    if (this.videoErrorTimer) {
      clearTimeout(this.videoErrorTimer);
      this.videoErrorTimer = null;
    }
  }
  dismissVideoError() { this.clearVideoError(); }
  private setVideoError(message: string) {
    this.videoError.set(message);
    if (this.videoErrorTimer) clearTimeout(this.videoErrorTimer);
    this.videoErrorTimer = setTimeout(() => this.clearVideoError(), 10000);
  }
}


